import { submitCardChoice, toggleCardChoice } from "../choices/cardChoice.js";
import { resolveFieldJudgmentChoice } from "../choices/fieldJudgmentChoice.js";
import { submitFocusChoice, toggleFocusChoice } from "../choices/focusChoice.js";
import { submitMeetingChoice, toggleMeetingChoice } from "../choices/meetingChoice.js";
import { submitQualityChoice, toggleQualityChoice } from "../choices/qualityChoice.js";
import type { Command, GameState } from "../types/index.js";
import { accept, reject } from "./reject.js";
import { resolveInn, toggleInn } from "./resolveInn.js";
import { resolveSendoff, resolveTraining, toggleSendoff, toggleTraining } from "./resolveSendoff.js";
import { resolveStables, toggleStables } from "./resolveStables.js";
import { resolveHandDisruption, toggleHandDisruption } from "./resolveHandDisruption.js";
import { resolveWorkerCycle, toggleWorkerCycle } from "./resolveWorkerCycle.js";
import { resolvePsychologicalCounseling, resolveSpecialTaskTeam, togglePsychologicalCounseling, toggleSpecialTaskTeam } from "./resolveIdleChoice.js";
import { continuePendingRepeat, withReshuffleLog } from "../choices/completion.js";

export function handleToggleSelection(
  state: GameState,
  command: Extract<Command, { type: "TOGGLE_SELECTION" }>,
): GameState {
  if (!state.run?.pendingChoice) return reject(state, "選択待ちではない");
  const pending = state.run.pendingChoice;
  const id = command.instanceId;

  switch (pending.kind) {
    case "cellar":
    case "caseExclude":
    case "knowledgeShare":
      return toggleCardChoice(state, pending, command);
    case "sendoff": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleSendoff(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "労働者カードを選択してください");
    }
    case "training": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleTraining(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "新卒または中堅を選択してください");
    }
    case "workerCycle": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleWorkerCycle(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "労働者カードを選択してください");
    }
    case "stables": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleStables(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "労働者カードを選択してください");
    }
    case "psychologicalCounseling": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = togglePsychologicalCounseling(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "PJ限定ではない窓際を選択してください");
    }
    case "specialTaskTeam": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleSpecialTaskTeam(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "窓際を選択してください");
    }
    case "inn": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleInn(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "捨てる手札は2枚までです");
    }
    case "handDisruption": {
      if (!id) return reject(state, "この選択ではトグルできない");
      const run = toggleHandDisruption(state.run, id);
      return run ? accept({ ...state, run }) : reject(state, "捨てる手札を選択してください");
    }
    case "meeting":
      return id ? toggleMeetingChoice(state, pending, id) : reject(state, "この選択ではトグルできない");
    case "fieldJudgment":
      return command.value === "use" || command.value === "return"
        ? resolveFieldJudgmentChoice(state, pending, command.value)
        : reject(state, "この選択ではトグルできない");
    case "focus":
      return command.value === "hand" || command.value === "aside"
        ? toggleFocusChoice(state, pending, command.value)
        : reject(state, "この選択ではトグルできない");
    case "quality":
      return command.value === "destroy" || command.value === "discard" || command.value === "return"
        ? toggleQualityChoice(state, pending, command.value)
        : reject(state, "この選択ではトグルできない");
  }
}

export function handleSubmitSelection(state: GameState): GameState {
  if (!state.run?.pendingChoice) return reject(state, "選択待ちではない");
  const pending = state.run.pendingChoice;

  switch (pending.kind) {
    case "cellar":
    case "caseExclude":
    case "knowledgeShare":
      return submitCardChoice(state, pending);
    case "sendoff":
      return accept({ ...state, run: continuePendingRepeat(resolveSendoff(state.run)) });
    case "training":
      return accept({ ...state, run: continuePendingRepeat(resolveTraining(state.run)) });
    case "workerCycle": {
      const result = resolveWorkerCycle(state.run);
      return accept({ ...state, run: withReshuffleLog(state.run, continuePendingRepeat(result.run), result.reshuffled) });
    }
    case "stables": {
      const result = resolveStables(state.run);
      return accept({ ...state, run: withReshuffleLog(state.run, continuePendingRepeat(result.run), result.reshuffled) });
    }
    case "psychologicalCounseling":
      return accept({ ...state, run: continuePendingRepeat(resolvePsychologicalCounseling(state.run)) });
    case "specialTaskTeam": {
      const result = resolveSpecialTaskTeam(state.run);
      return accept({ ...state, run: withReshuffleLog(state.run, continuePendingRepeat(result.run), result.reshuffled) });
    }
    case "inn": {
      const run = resolveInn(state.run);
      return run
        ? accept({ ...state, run: continuePendingRepeat(run) })
        : reject(state, "捨てる手札を2枚選んでください");
    }
    case "handDisruption": {
      const run = resolveHandDisruption(state.run);
      return run ? accept({ ...state, run }) : reject(state, "捨てる手札を必要枚数選んでください");
    }
    case "meeting":
      return submitMeetingChoice(state, pending);
    case "focus":
      return submitFocusChoice(state, pending);
    case "quality":
      return submitQualityChoice(state, pending);
    case "fieldJudgment":
      return reject(state, "現場判断の選択をしてください");
  }
}
